import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  TeacherLoadingState,
  TeacherPageShell,
  useTeacherAdventureOverview,
} from './teacherAdventureShared';

export const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date') ?? '';
  const selectedLessonRunId = searchParams.get('lessonRunId') ?? '';
  const { overview, isLoading, loadError, reloadOverview } = useTeacherAdventureOverview(
    selectedDate || undefined,
    selectedLessonRunId || undefined
  );
  const [isLessonStarting, setIsLessonStarting] = useState(false);
  const [lessonError, setLessonError] = useState('');

  useEffect(() => {
    const responseDate = overview.filter?.date ?? '';
    const responseLessonRunId = overview.filter?.lessonRunId ?? '';
    if (!responseDate || (selectedDate === responseDate && selectedLessonRunId === responseLessonRunId)) {
      return;
    }

    const nextParams = new URLSearchParams();
    nextParams.set('date', responseDate);
    if (responseLessonRunId) {
      nextParams.set('lessonRunId', responseLessonRunId);
    }
    setSearchParams(nextParams, { replace: true });
  }, [overview.filter?.date, overview.filter?.lessonRunId, selectedDate, selectedLessonRunId, setSearchParams]);

  const summary = useMemo(() => {
    const preparationStudents = new Set(overview.preparationPosterRecords.map((item) => item.studentId));
    const imageStudents = new Set([
      ...overview.dangerImageRecords.map((item) => item.studentId),
      ...overview.escapeImageRecords.map((item) => item.studentId),
    ]);

    return {
      preparationPosters: overview.preparationPosterRecords.length,
      dangerImages: overview.dangerImageRecords.length,
      escapeImages: overview.escapeImageRecords.length,
      activeStudents: new Set([...preparationStudents, ...imageStudents]).size,
    };
  }, [overview]);

  const buildDatePath = (path: string, options?: { includeLessonRunId?: boolean }) => {
    const nextParams = new URLSearchParams();
    if (selectedDate) {
      nextParams.set('date', selectedDate);
    }
    if (options?.includeLessonRunId !== false && selectedLessonRunId) {
      nextParams.set('lessonRunId', selectedLessonRunId);
    }

    const query = nextParams.toString();
    return query ? `${path}?${query}` : path;
  };

  const handleDateChange = (nextDate: string) => {
    if (nextDate) {
      setSearchParams({ date: nextDate });
      return;
    }

    setSearchParams({});
  };

  const handleLessonRunChange = (nextLessonRunId: string) => {
    const nextParams = new URLSearchParams();
    const responseDate = overview.filter?.date ?? selectedDate;
    if (responseDate) {
      nextParams.set('date', responseDate);
    }
    if (nextLessonRunId) {
      nextParams.set('lessonRunId', nextLessonRunId);
    }
    setSearchParams(nextParams);
  };

  const handleCreateLesson = async () => {
    if (isLessonStarting) {
      return;
    }

    setIsLessonStarting(true);
    setLessonError('');

    try {
      const response = await api.post('/submissions/teacher-adventure-lessons', {
        ...(selectedDate ? { date: selectedDate } : {}),
      });
      const nextDate = typeof response.data?.lessonDate === 'string' ? response.data.lessonDate : selectedDate;
      const nextLessonRunId =
        typeof response.data?.lessonRunId === 'string' ? response.data.lessonRunId : '';
      if (nextDate) {
        const nextParams = new URLSearchParams();
        nextParams.set('date', nextDate);
        if (nextLessonRunId) {
          nextParams.set('lessonRunId', nextLessonRunId);
        }
        setSearchParams(nextParams, { replace: true });
        return;
      }
      await reloadOverview();
    } catch (error) {
      console.error(error);
      setLessonError('新开一节课失败，请稍后重试。');
    } finally {
      setIsLessonStarting(false);
    }
  };

  if (isLoading || loadError) {
    return (
      <TeacherPageShell
        title="教师端冒险总览"
        description="这里保留探险准备海报、设计惊险情节、统计分析和展开逃生过程四个板块。"
        backToTeacherHome={false}
      >
        <TeacherLoadingState error={loadError} loadingText="正在加载教师端总览..." />
      </TeacherPageShell>
    );
  }

  const availableLessonRuns = overview.filter?.availableLessonRuns ?? [];
  const currentLessonRunId = overview.filter?.lessonRunId ?? '';

  const cards = [
    {
      title: '探险准备海报',
      description: '查看学生完成准备后的出发海报与摘要',
      count: `${summary.preparationPosters}`,
      actionLabel: '进入探险准备海报页面',
      path: buildDatePath('/teacher/preparation-posters'),
      className: 'bg-rose-600 text-white',
      subClassName: 'text-rose-100',
    },
    {
      title: '设计惊险情节',
      description: '查看学生上传的惊险情节图片',
      count: `${summary.dangerImages}`,
      actionLabel: '进入设计惊险情节页面',
      path: buildDatePath('/teacher/danger-images'),
      className: 'bg-emerald-700 text-white',
      subClassName: 'text-emerald-100',
    },
    {
      title: '统计分析',
      description: '查看课堂看板与教研分析双标签统计',
      count: `${summary.activeStudents}`,
      actionLabel: '进入统计分析页面',
      path: buildDatePath('/teacher/analytics'),
      className: 'bg-indigo-700 text-white',
      subClassName: 'text-indigo-100',
    },
    {
      title: '展开逃生过程',
      description: '查看学生上传的逃生过程图片',
      count: `${summary.escapeImages}`,
      actionLabel: '进入展开逃生过程页面',
      path: buildDatePath('/teacher/escape-images'),
      className: 'bg-amber-500 text-slate-900',
      subClassName: 'text-amber-950/70',
    },
  ];

  return (
    <TeacherPageShell
      title="教师端冒险总览"
      description="这里保留探险准备海报、设计惊险情节、统计分析和展开逃生过程四个板块。"
      backToTeacherHome={false}
    >
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <button
            key={card.path}
            type="button"
            onClick={() => navigate(card.path)}
            aria-label={card.actionLabel}
            className={`rounded-[1.8rem] px-6 py-6 text-left shadow-sm transition hover:-translate-y-0.5 ${card.className}`}
          >
            <div className={`text-sm ${card.subClassName}`}>{card.title}</div>
            <div className="mt-3 text-4xl font-black">{card.count}</div>
            <p className={`mt-3 text-sm leading-6 ${card.subClassName}`}>{card.description}</p>
          </button>
        ))}
      </section>

      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">当前课堂课次</h2>
            <p className="mt-1 text-sm text-slate-500">首页会按课次读取数据。同一天多节课时，可以在这里切换。</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-2 text-sm text-slate-600">
              <span>课程日期</span>
              <input
                type="date"
                aria-label="课程日期"
                value={selectedDate}
                onChange={(event) => handleDateChange(event.target.value)}
                className="rounded-xl border border-stone-200 bg-white px-3 py-1 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              />
            </label>
            {availableLessonRuns.length > 0 ? (
              <label className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-3 py-2 text-sm text-slate-600">
                <span>课次</span>
                <select
                  aria-label="课次"
                  value={selectedLessonRunId || currentLessonRunId}
                  onChange={(event) => handleLessonRunChange(event.target.value)}
                  className="rounded-xl border border-stone-200 bg-white px-3 py-1 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                >
                  {availableLessonRuns.map((lessonRun) => (
                    <option key={lessonRun.lessonRunId} value={lessonRun.lessonRunId}>
                      {lessonRun.lessonLabel}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {selectedDate ? (
              <button
                type="button"
                onClick={() => handleDateChange('')}
                className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                回到当前课
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">课堂管理</h2>
            <p className="mt-1 text-sm text-slate-500">
              新开一节课后，学生端只会看到当前课次的数据；教师端仍可按日期查看历史作品和勋章。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleCreateLesson}
              className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:border-emerald-400 hover:bg-emerald-100"
            >
              {isLessonStarting ? '正在新开课程...' : '新开一节课'}
            </button>
          </div>
        </div>

        {lessonError ? <div className="mb-4 text-sm text-rose-600">{lessonError}</div> : null}

        <div className="grid gap-4 md:grid-cols-2">
          <article className="rounded-[1.5rem] border border-stone-200 bg-stone-50 px-5 py-5">
            <div className="text-sm text-slate-500">活跃学生</div>
            <div className="mt-2 text-3xl font-black text-slate-900">{summary.activeStudents}</div>
          </article>
          <article className="rounded-[1.5rem] border border-stone-200 bg-stone-50 px-5 py-5">
            <div className="text-sm text-slate-500">图片总数</div>
            <div className="mt-2 text-3xl font-black text-slate-900">
              {summary.dangerImages + summary.escapeImages}
            </div>
          </article>
        </div>
      </section>

    </TeacherPageShell>
  );
};
